import React from "react";
import { motion } from "framer-motion";

const COIN_URL =
  "https://customer-assets.emergentagent.com/job_1fbc6953-ea45-495f-875d-b379b35a9c27/artifacts/08v50aom_CLOUD%20GM%20COIN_GM-COIN-6.png";

/**
 * RotatingCoin - the brand coin, animated with framer-motion.
 * Variants:
 *  - "hero": large floating + 3D rotation + glow ring
 *  - "small": small spinning version for navbar / inline
 *  - "static": gentle bob with shimmer aura
 */
const RotatingCoin = ({ variant = "hero", size, className = "", testId = "rotating-coin" }) => {
  const dimensions = {
    hero: size || 360,
    small: size || 36,
    static: size || 96,
  };
  const px = dimensions[variant];

  if (variant === "small") {
    return (
      <motion.div
        data-testid={testId}
        className={`relative inline-flex shrink-0 ${className}`}
        style={{ width: px, height: px }}
        animate={{ rotateY: [0, 360] }}
        transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
      >
        <img
          src={COIN_URL}
          alt="CLOUD GM COIN"
          width={px}
          height={px}
          className="w-full h-full object-contain coin-shadow"
          draggable="false"
        />
      </motion.div>
    );
  }

  if (variant === "static") {
    return (
      <motion.div
        data-testid={testId}
        className={`relative inline-flex shrink-0 ${className}`}
        style={{ width: px, height: px }}
        animate={{ y: [-6, 6, -6], rotate: [-3, 3, -3] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      >
        <div
          aria-hidden
          className="absolute inset-0 rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(212,175,55,0.35) 0%, transparent 70%)",
            filter: "blur(20px)",
          }}
        />
        <img
          src={COIN_URL}
          alt="CLOUD GM COIN"
          width={px}
          height={px}
          className="relative w-full h-full object-contain coin-shadow"
          draggable="false"
        />
      </motion.div>
    );
  }

  // hero
  return (
    <div
      data-testid={testId}
      className={`relative inline-flex items-center justify-center ${className}`}
      style={{ width: px, height: px, perspective: 1200 }}
    >
      {/* Soft pulsing aura */}
      <motion.div
        aria-hidden
        className="absolute inset-0 rounded-full"
        animate={{ scale: [1, 1.08, 1], opacity: [0.55, 0.85, 0.55] }}
        transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
        style={{
          background:
            "radial-gradient(circle, rgba(255,215,0,0.35) 0%, rgba(212,175,55,0.18) 40%, transparent 70%)",
          filter: "blur(36px)",
        }}
      />

      {/* Outer rotating ring */}
      <motion.div
        aria-hidden
        className="absolute inset-[-8%] rounded-full"
        animate={{ rotate: 360 }}
        transition={{ duration: 28, repeat: Infinity, ease: "linear" }}
        style={{
          border: "1px dashed rgba(212,175,55,0.35)",
        }}
      />
      <motion.div
        aria-hidden
        className="absolute inset-[-2%] rounded-full"
        animate={{ rotate: -360 }}
        transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
        style={{
          border: "1px solid rgba(212,175,55,0.18)",
        }}
      />

      {/* Floating + 3D-rotating coin */}
      <motion.div
        className="relative"
        style={{ width: px, height: px, transformStyle: "preserve-3d" }}
        animate={{ y: [-12, 12, -12] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      >
        <motion.div
          className="w-full h-full"
          animate={{ rotateY: [0, 360] }}
          transition={{ duration: 14, repeat: Infinity, ease: "linear" }}
          style={{ transformStyle: "preserve-3d" }}
        >
          <img
            src={COIN_URL}
            alt="CLOUD GM COIN"
            width={px}
            height={px}
            className="w-full h-full object-contain coin-shadow select-none"
            draggable="false"
          />
        </motion.div>
      </motion.div>
    </div>
  );
};

export default RotatingCoin;
